import {
  CONFIRMED_EMPTY_GATE_BY_KEY,
  collectQuestions,
  DIFFERENT_VALUE,
  MY_SAME_QUESTION,
  NO_VALUE,
  OWNS_OTHER_COMPANIES_ID,
  PAYLOAD_TYPE,
  RemoteFileValue,
  SAME_AS_CURRENT,
  TAX_ONBOARDING_ENTITIES_KEY,
  TAX_SETUP_QUESTIONS,
  TaxSetupQuestion,
  TaxSetupValues,
  YES_VALUE,
} from "v2/constants/taxSetupQuestions";

export type TaxSetupPayloadValue = string | string[] | boolean | number;
export type TaxSetupPayload = Record<string, TaxSetupPayloadValue>;

export type TaxSetupPayloadTree = Record<
  string,
  Record<string, TaxSetupPayload>
>;

const TAX_YEAR_END_KEY = "tax_year_end";

const ALL_QUESTIONS = [
  ...collectQuestions(TAX_SETUP_QUESTIONS),
  MY_SAME_QUESTION,
];

const QUESTION_BY_ID: Record<string, TaxSetupQuestion> = Object.fromEntries(
  ALL_QUESTIONS.map((question) => [question.id, question])
);

const QUESTION_BY_PAYLOAD_KEY: Record<string, TaxSetupQuestion> =
  Object.fromEntries(
    ALL_QUESTIONS.filter((question) => question.payloadKey).map((question) => [
      question.payloadKey,
      question,
    ])
  );

type FileFollowUp = { parentId: string; childId: string; trigger: string };

const FILE_FOLLOW_UPS: FileFollowUp[] = ALL_QUESTIONS.flatMap((question) => {
  const child = question.followUp?.question;
  const trigger = question.followUp?.triggerValues?.[0];

  if (!child || child.fieldType !== "file" || !trigger) return [];

  return [{ parentId: question.id, childId: child.id, trigger }];
});

const isEmptyValue = (value: string | string[]): boolean =>
  Array.isArray(value) ? value.length === 0 : value === "";

const isNumericAnswer = (
  question: TaxSetupQuestion,
  value: string | string[]
): boolean => {
  if (question.payloadType !== PAYLOAD_TYPE.NUMBER) return true;

  return !Array.isArray(value) && Number.isFinite(Number(value));
};

const toPayloadValue = (
  question: TaxSetupQuestion,
  value: string | string[]
): TaxSetupPayloadValue => {
  if (question.payloadType === PAYLOAD_TYPE.NUMBER) return Number(value);
  if (question.payloadType === PAYLOAD_TYPE.BOOL_YES)
    return value === YES_VALUE;
  if (question.payloadType === PAYLOAD_TYPE.BOOL_NO) return value === NO_VALUE;
  if (question.payloadType === PAYLOAD_TYPE.BOOL_SAME)
    return value === SAME_AS_CURRENT;
  return value;
};

export const toPatchEntry = (
  questionId: string,
  value: string | string[]
): { key: string; value: TaxSetupPayloadValue } | null => {
  const question = QUESTION_BY_ID[questionId];
  if (!question?.payloadKey) return null;

  return { key: question.payloadKey, value: toPayloadValue(question, value) };
};

const fromPayloadValue = (
  question: TaxSetupQuestion,
  value: unknown
): string | string[] => {
  const isEmpty =
    value === null ||
    value === undefined ||
    (Array.isArray(value) && value.length === 0);
  if (isEmpty) return question.type === "multi" ? [] : "";

  if (question.payloadType === PAYLOAD_TYPE.BOOL_YES)
    return value === true ? YES_VALUE : NO_VALUE;
  if (question.payloadType === PAYLOAD_TYPE.BOOL_NO)
    return value === true ? NO_VALUE : YES_VALUE;
  if (question.payloadType === PAYLOAD_TYPE.BOOL_SAME)
    return value === true ? SAME_AS_CURRENT : DIFFERENT_VALUE;
  if (Array.isArray(value)) return value.map(String);
  return String(value);
};

const isFilled = (value: unknown): boolean =>
  Array.isArray(value) ? value.length > 0 : Boolean(value);

const isRemoteFileValue = (value: unknown): value is RemoteFileValue =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  "uuid" in value &&
  "name" in value;

const applyFollowUpGates = (
  questions: TaxSetupQuestion[],
  values: TaxSetupValues,
  isAnsweredByPayload: (question: TaxSetupQuestion) => boolean
): void => {
  questions.forEach((question) => {
    if (question.followUp)
      applyFollowUpGates(
        [question.followUp.question],
        values,
        isAnsweredByPayload
      );

    (question.followUpGroups ?? []).forEach((group) =>
      applyFollowUpGates(group.questions, values, isAnsweredByPayload)
    );

    if (!question.followUp) return;
    if (isAnsweredByPayload(question)) return;

    const childFilled = collectQuestions([question.followUp.question]).some(
      (child) => isFilled(values[child.id])
    );
    const trigger = question.followUp.triggerValues?.[0];

    if (childFilled && trigger && !isFilled(values[question.id])) {
      values[question.id] = trigger;
    }
  });
};

export const fromScopePayload = (
  payload: Record<string, unknown>
): TaxSetupValues => {
  const values: TaxSetupValues = {};

  const payloadKeys = new Set(Object.keys(payload));

  const isAnsweredByPayload = (question: TaxSetupQuestion): boolean =>
    Boolean(question.payloadKey && payloadKeys.has(question.payloadKey));

  Object.entries(payload).forEach(([beKey, value]) => {
    const question = QUESTION_BY_PAYLOAD_KEY[beKey];
    if (!question) return;

    if (question.fieldType === "file") {
      if (isRemoteFileValue(value)) values[question.id] = value;
      return;
    }

    values[question.id] = fromPayloadValue(question, value);

    // [] (vs. omitted/null) means the customer was asked and confirmed none —
    // that only shows up correctly if the question's own Yes/No gate reflects
    // "No" instead of looking unanswered.
    const gateId = CONFIRMED_EMPTY_GATE_BY_KEY[beKey];
    const isConfirmedEmpty = Array.isArray(value) && value.length === 0;
    if (gateId && isConfirmedEmpty) values[gateId] = NO_VALUE;
  });

  FILE_FOLLOW_UPS.forEach(({ parentId, childId, trigger }) => {
    const parent = QUESTION_BY_ID[parentId];
    if (parent && isAnsweredByPayload(parent)) return;

    if (isFilled(values[childId])) values[parentId] = trigger;
  });

  applyFollowUpGates(TAX_SETUP_QUESTIONS, values, isAnsweredByPayload);

  const onboardingEntities = payload[TAX_ONBOARDING_ENTITIES_KEY];
  if (Array.isArray(onboardingEntities)) {
    values[OWNS_OTHER_COMPANIES_ID] =
      onboardingEntities.length > 0 ? YES_VALUE : NO_VALUE;
  }

  return values;
};

export const buildScopePayload = (
  scopeValues: TaxSetupValues,
  isPastSeason = false
): TaxSetupPayload => {
  const payload: TaxSetupPayload = {};

  Object.entries(scopeValues).forEach(([questionId, value]) => {
    const question = QUESTION_BY_ID[questionId];
    if (!question?.payloadKey) return;

    if (question.fieldType === "file") return;
    if (typeof value !== "string" && !Array.isArray(value)) return;
    if (isPastSeason && question.payloadKey === TAX_YEAR_END_KEY) return;

    // An empty answer is normally omitted (no value given). But for a question
    // whose Yes/No gate was answered "No", [] is the real, deliberate answer —
    // it has to be sent, not skipped, so the backend can tell "confirmed none"
    // apart from "never asked". The gate being "No" wins over whatever is still
    // sitting in the child field (e.g. a stale selection from a prior "Yes").
    const gateId = CONFIRMED_EMPTY_GATE_BY_KEY[question.payloadKey];
    const isConfirmedEmptyAnswer =
      Boolean(gateId) && scopeValues[gateId] === NO_VALUE;
    if (isConfirmedEmptyAnswer) {
      payload[question.payloadKey] = toPayloadValue(question, []);
      return;
    }
    if (isEmptyValue(value)) return;
    if (!isNumericAnswer(question, value)) return;

    payload[question.payloadKey] = toPayloadValue(question, value);
  });

  return payload;
};
